import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useAppSelector } from '@/store';
import { selectCreativeJobsMap, type CreativeJob } from '@/store/slices/creativeJobs';

import { siteDefaultLanguage } from '../../ad-language-api';
import { adBrandFrom, subjectLandingUrl } from '../../ad-subjects';
import { newestLockedVersion, withCreated } from '../../ad-versions';
import { adTabRoutes, type AdView } from '../../asset-routes';
import type { AdSubject, AdSubjectType } from '../../banner-kit-api';
import { applyBrandTheme, fetchBrandTheme, type BrandTheme } from '../../brand-theme-api';
import type { CreativeItem } from '../../creative-api';
import { revealOnLoad } from '../../reveal-media';
import { useSiteLanguages } from '../../hooks/useSiteLanguages';
import { html5BannerPoster, newHtml5BannerParams, stylesFor } from '../../html5-banners';
import {
  bannerTemplates,
  fetchPlayableTemplates,
  isHtml5Banner,
  upsertPlayable,
  type Playable,
  type PlayableTemplate,
  type PlayableTemplatesResponse,
} from '../../playable-api';
import { useAssetLibraryStats } from '../../useAssetLibraryStats';
import { STATS_SHEET_PARAM, useSheetParam } from '../../useSheetParam';
import { usePlayableLibrary } from '../../usePlayableLibrary';
import { AdLibraryShell, type AdCardState } from '../AdLibraryShell';
import { AdSubjectFlow } from '../AdSubjectFlow';
import { AssetCardStats, AssetCardStatsSkeleton, AssetStatsSheet } from '../AssetStats';
import { LumioMark } from '../LumioMark';
import { PlayableComposer } from '../PlayableComposer';
import { StyleStep } from './StyleStep';
import * as S from './Html5BannerLibrary.styles';

export interface Html5BannerLibraryProps {
  tenantId?: number;
  brandName: string;
  brandDomain?: string;
  brandLogoUrl?: string;
  /** The brand's promotion pool (the subjects a banner can advertise, and the tracking link's `cta_href`). */
  items: CreativeItem[];
  itemsLoaded: boolean;
  /** The deep-linked view (library grid, create new, or a banner by id), derived from the URL by the page. */
  view: AdView;
  onBuyCredits: (needed?: number) => void;
  onNotify: (message: string) => void;
}

const isActive = (job: CreativeJob | undefined): boolean => job?.status === 'queued' || job?.status === 'running';

/** A banner's card state: an art run in flight, generated art (a locked version), or a draft. Pure. */
const cardState = (p: Playable, jobsById: Record<string, CreativeJob>): AdCardState => {
  if (isActive(p.job_id ? jobsById[p.job_id] : undefined)) return 'generating';
  return newestLockedVersion(p.ad_versions) ? 'ready' : 'draft';
};

/**
 * Marketing Assets > HTML5 Banners: the brand's live-text animated banners (playables of a `banner` template) on the
 * shared ad library shell. Creating one NAVIGATES to `/html5-banners/new`: what it advertises (the Image Banners
 * pickers), then its style (cards playing each style live), then the draft is minted WITH that style (and the website
 * theme) and the URL swapped to its id in place, so the composer opens on it and Lumio paints its art there.
 * Opening a banner navigates to `/html5-banners/:id`. The URL stays the single source of truth.
 */
export const Html5BannerLibrary = ({
  tenantId,
  brandName,
  brandDomain,
  brandLogoUrl,
  items,
  itemsLoaded,
  view,
  onBuyCredits,
  onNotify,
}: Html5BannerLibraryProps): React.ReactElement => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const routes = adTabRoutes('html5-banners');
  const {
    items: playables,
    loading,
    loaded,
    settled,
    listFailed,
    refresh,
    remove,
    restore,
    purge,
    patch,
  } = usePlayableLibrary(tenantId);
  const jobsById = useAppSelector(selectCreativeJobsMap);
  const viewId = view.kind === 'edit' ? view.id : undefined;

  // The template registry (the styles, their labels and the runtime the cards play in): static per deploy.
  const [registry, setRegistry] = React.useState<PlayableTemplatesResponse | null>(null);
  const [registryFailed, setRegistryFailed] = React.useState(false);
  React.useEffect(() => {
    let live = true;
    void fetchPlayableTemplates().then((r) => {
      if (!live) return;
      if (r) setRegistry(r);
      else setRegistryFailed(true);
    });
    return () => {
      live = false;
    };
  }, []);
  const templates = registry?.templates;
  const styles = React.useMemo(() => bannerTemplates(templates ?? []), [templates]);

  // `new`: the subject, then the style; the draft is minted on the style and the URL
  // swapped to its id WITHOUT navigating (see the Image Banners library for why). `createdRef` stops a double mint;
  // `createGenRef` lets a stale mint tell it is no longer the current attempt.
  const [created, setCreated] = React.useState<Playable | null>(null);
  const [minting, setMinting] = React.useState(false);
  const createdRef = React.useRef(false);
  const createGenRef = React.useRef(0);
  const [chosen, setChosen] = React.useState<{ type: AdSubjectType; subject: AdSubject } | null>(null);
  // The website theme the new banner (and its style cards) start from; undefined while it is read.
  const [theme, setTheme] = React.useState<BrandTheme | null | undefined>(undefined);

  // The library does not remount across /html5-banners <-> /new <-> /:id (the page keys it by tab), so returning to
  // the grid resets the new-banner session; the grid is the one view with no mint in flight.
  React.useEffect(() => {
    if (view.kind !== 'library') return;
    createdRef.current = false;
    createGenRef.current += 1; // invalidate any in-flight mint
    setCreated(null);
    setMinting(false);
    setChosen(null);
  }, [view.kind]);

  // The brand's website theme prefills every new banner; read early (cached per tenant, never throws).
  React.useEffect(() => {
    if (!tenantId) {
      setTheme(null);
      return undefined;
    }
    let live = true;
    void fetchBrandTheme(tenantId).then((th) => {
      if (live) setTheme(th);
    });
    return () => {
      live = false;
    };
  }, [tenantId]);

  // `edit`: resolved from the list once both the list and the registry are known (a banner is a playable whose
  // template is a banner style); otherwise leave to the grid with a notice.
  React.useEffect(() => {
    // Without the registry no playable can be told to be a banner: back to the grid, which says it couldn't load.
    if (view.kind === 'edit' && registryFailed) {
      navigate(routes.library(), { replace: true });
      return;
    }
    if (view.kind !== 'edit' || !viewId || !loaded || loading || !registry) return;
    // The asset minted here moments ago is not missing while the list catches up: opening its stats or links sheet moves
    // the address from /new to its id before the post-mint refresh has landed it.
    if (created && created.tenant_id === String(tenantId) && created.id === viewId) return;
    if (!playables.some((p) => p.id === viewId && isHtml5Banner(p, registry.templates))) {
      onNotify(t("That HTML5 banner couldn't be found."));
      navigate(routes.library(), { replace: true });
    }
  }, [
    view.kind,
    viewId,
    loaded,
    loading,
    registry,
    registryFailed,
    playables,
    created,
    tenantId,
    routes,
    onNotify,
    navigate,
    t,
  ]);

  const goToLibrary = React.useCallback((): void => navigate(routes.library()), [navigate, routes]);
  const patchBanner = React.useCallback(
    (id: string, fields: Partial<Playable>): void => {
      patch(id, fields);
      setCreated((cur) => (cur && cur.id === id ? { ...cur, ...fields } : cur));
    },
    [patch],
  );
  const startNew = React.useCallback((): void => navigate(routes.create()), [navigate, routes]);
  const openBanner = React.useCallback((p: Playable): void => navigate(routes.edit(p.id)), [navigate, routes]);

  const brandFor = React.useCallback(
    (th: BrandTheme | null | undefined) => applyBrandTheme(adBrandFrom(brandName, brandDomain, brandLogoUrl), th),
    [brandName, brandDomain, brandLogoUrl],
  );

  // The website's languages: the new banner is written in the default one (the style cards play it in it once read).
  const siteLanguages = useSiteLanguages(tenantId);
  const draftLanguage = siteLanguages.known ? siteLanguages.defaultLanguage : '';

  // The styles that fit the chosen subject, and the document the new banner would start from (the style cards play
  // it); null while the theme is read.
  const offered = React.useMemo(() => (chosen ? stylesFor(styles, chosen.type) : []), [chosen, styles]);
  const draft = React.useMemo(
    () =>
      chosen && offered[0] && theme !== undefined
        ? newHtml5BannerParams(
            chosen.type,
            chosen.subject,
            brandFor(theme),
            offered[0],
            subjectLandingUrl(chosen.subject, brandDomain),
            draftLanguage,
          )
        : null,
    [chosen, offered, theme, brandFor, brandDomain, draftLanguage],
  );

  const mint = React.useCallback(
    (style: PlayableTemplate): void => {
      if (!tenantId || !chosen || createdRef.current) return;
      createdRef.current = true;
      const gen = (createGenRef.current += 1);
      setMinting(true);
      const { type, subject } = chosen;
      const name = subject.name || t('Untitled HTML5 banner');
      const fail = (): void => {
        // A stale attempt must not release a newer attempt's re-entry lock (that would allow a duplicate draft).
        if (gen !== createGenRef.current) return;
        createdRef.current = false;
        setMinting(false);
        onNotify(t("That couldn't be created. Please try again."));
      };
      // The website theme (cached, bounded wait, null when the site has none) and the website's default language (the
      // ad's language; '' when it could not be read, filled in by the composer later) first, then the draft WITH its
      // style (a playable with no banner style would be listed as a mini-game).
      void Promise.all([fetchBrandTheme(tenantId), siteDefaultLanguage(tenantId)]).then(([th, language]) => {
        if (gen !== createGenRef.current) return;
        const adParams = newHtml5BannerParams(
          type,
          subject,
          brandFor(th),
          style,
          subjectLandingUrl(subject, brandDomain),
          language,
        );
        void upsertPlayable(tenantId, {
          name,
          status: 'draft',
          image_url: subject.image_url,
          ad_params: adParams,
        }).then(
          (id) => {
            if (gen !== createGenRef.current) return;
            if (!id) {
              fail();
              return;
            }
            setMinting(false);
            setCreated({
              id,
              tenant_id: String(tenantId),
              name,
              media_type: 'playable',
              image_url: subject.image_url,
              status: 'draft',
              archived: false,
              ad_params: adParams,
              ad_versions: [],
              stats: { impressions: 0, interactions: 0, reveals: 0, clicks: 0 },
            });
            window.history.replaceState(window.history.state, '', routes.edit(id));
            refresh();
          },
          fail, // `fail` covers the request only: once the row exists, nothing may unlock a second mint
        );
      });
    },
    [tenantId, chosen, brandFor, brandDomain, routes, refresh, onNotify, t],
  );

  // The banner the editor opens: the freshly minted draft for `new` (the live list entry once the post-mint refresh
  // lands it), else the one matching the :id. A cold deep link waits for the list.
  const editing = view.kind !== 'library';
  // The freshly minted draft counts only for the brand it was made for: right after a brand switch it must not stand
  // in for the (still empty) new brand's list, or its editor would render, and save, under the new brand.
  const createdHere = created && created.tenant_id === String(tenantId) ? created : null;
  const reopen: Playable | undefined =
    view.kind === 'new'
      ? createdHere
        ? withCreated(playables, createdHere)
        : undefined
      : viewId && createdHere?.id === viewId
        ? withCreated(playables, createdHere)
        : viewId && registry
          ? playables.find((p) => p.id === viewId && isHtml5Banner(p, registry.templates))
          : undefined;

  const composerNode =
    editing && reopen ? (
      <PlayableComposer
        key={`${tenantId}:${reopen.id}`}
        kind="banner"
        tenantId={tenantId}
        playable={reopen}
        brandName={brandName}
        brandDomain={brandDomain}
        items={items}
        patch={patchBanner}
        onClose={goToLibrary}
        onBuyCredits={onBuyCredits}
        onNotify={onNotify}
      />
    ) : null;

  // The HTML5 banners only: playables of a known `banner` style (the rest are mini-games).
  const banners = templates ? playables.filter((p) => isHtml5Banner(p, templates)) : [];
  const archivedCount = banners.filter((p) => p.archived).length;
  // Each card's tracking-link figures (one grouped read for the grid; an entry iff the banner has a link), re-read
  // when a composer closes; a card's figures open the banner's stats sheet.
  const liveIds = banners.filter((p) => !p.archived).map((p) => p.id);
  const stats = useAssetLibraryStats('banner', liveIds, {
    onGrid: view.kind === 'library',
    idsLoaded: loaded && !listFailed,
  });
  // The stats sheet open over the grid is the address's (`?stats=<id>`), so Back from one of its deep links reopens it.
  // Read on the grid only: a composer open over it reads the same key for its own sheet.
  const statsSheet = useSheetParam(STATS_SHEET_PARAM);
  const statsForId = view.kind === 'library' ? statsSheet.value : null;
  const statsFor = statsForId ? banners.find((p) => p.id === statsForId && !p.archived) : undefined;
  const styleLabel = (id: string): string | undefined => templates?.find((tpl) => tpl.id === id)?.label;
  const creating = view.kind === 'new' && !created;

  return (
    <>
      <AdLibraryShell<Playable>
        kind="html5_banner"
        items={banners}
        // The final list needs the brand's playables AND the registry that tells a banner from a mini-game.
        ready={settled && (templates !== undefined || registryFailed)}
        title={t('Your HTML5 banners')}
        intro={t(
          'Every HTML5 banner you have made, animated in every display size. Open one to edit it, generate its art or export it, archive it, or create a new one.',
        )}
        archivedTitle={t('Archived HTML5 banners ({{n}})', { n: archivedCount })}
        archivedEmptyText={t('No archived HTML5 banners.')}
        emptyText={
          // A failed registry read hides every banner: never shown as "none yet" (a reload reads it again).
          registryFailed || listFailed
            ? t("Your HTML5 banners couldn't be loaded. Reload the page to try again.")
            : t('No HTML5 banners yet. Create your first one.')
        }
        loadingText={t('Loading your HTML5 banners…')}
        createLabel={t('Create new')}
        createAriaLabel={t('Create a new HTML5 banner')}
        untitledName={t('Untitled HTML5 banner')}
        archiveConfirmTitle={t('Archive this HTML5 banner?')}
        archiveConfirmBody={t(
          'It will be moved to Archived HTML5 banners, where you can restore it later or permanently delete it.',
        )}
        posterOf={(p) => {
          const url = html5BannerPoster(p);
          return url ? (
            <S.PosterImg key={url} {...revealOnLoad} src={url} alt="" />
          ) : (
            <S.PosterPlaceholder>
              {/* Lumio brand mark for this AI feature (never an approximated sparkle/star). */}
              <LumioMark size={22} />
              <S.PosterEmpty text="Not generated yet" />
            </S.PosterPlaceholder>
          );
        }}
        thumbOf={html5BannerPoster}
        metaOf={(p) => {
          const label = styleLabel(p.ad_params.template_id);
          return label
            ? t('{{style}} · {{count}} sizes', { style: label, count: p.ad_params.formats.length })
            : t('HTML5 banner');
        }}
        stateOf={(p) => cardState(p, jobsById)}
        cardFooterSkeleton={<AssetCardStatsSkeleton />}
        cardFooterOf={(p) => (
          <AssetCardStats
            loaded={stats.loaded}
            failed={stats.failed}
            stats={stats.byId[p.id]}
            hint={newestLockedVersion(p.ad_versions) ? 'get-link' : 'generate-first'}
            onOpen={() => statsSheet.open(p.id)}
          />
        )}
        onCreate={startNew}
        onOpen={openBanner}
        remove={remove}
        restore={restore}
        purge={purge}
        jobsById={jobsById}
      />

      {composerNode}

      {statsFor ? (
        <AssetStatsSheet
          kind="banner"
          creativeId={statsFor.id}
          name={statsFor.name}
          onClose={() => statsSheet.close()}
        />
      ) : null}

      {/* A NEW banner: what it advertises, then its style (the draft is minted on the style; Lumio paints its art). */}
      {creating && !chosen ? (
        <AdSubjectFlow
          items={items}
          itemsLoaded={itemsLoaded}
          promotionTitle={t('Choose the promotion this HTML5 banner advertises')}
          promotionHint={t('Pick one. Its key art and copy go on every size.')}
          onChosen={(type, subject) => setChosen({ type, subject })}
          subjectTypes={['promotion', 'game']}
          onClose={goToLibrary}
        />
      ) : null}

      {creating && chosen ? (
        <StyleStep
          styles={offered}
          runtime={registry?.runtime ?? null}
          loaded={registry !== null}
          failed={registryFailed}
          doc={draft}
          busy={minting}
          onPick={mint}
          onBack={() => setChosen(null)}
          onClose={goToLibrary}
        />
      ) : null}
    </>
  );
};

export default Html5BannerLibrary;
